import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, ShieldAlert } from "lucide-react";
import R1617Mark from "@/components/R1617Mark";
import { api } from "@/lib/api";

/**
 * Research Select story landing (mock v3).
 *
 * Palette:
 *   • #F1E5C7 cream ground
 *   • #1B1614 ink — headings AND small uppercase kickers (fixed low-contrast)
 *   • #4A4340 cool warm dark grey — body copy
 *   • #7A5715 deep gold — italic emphasis in serif copy only
 *   • #DDA94E bright gold — CTA fills on cream
 *
 * Changes vs v2:
 *   • 3 chain cards (dropped TX Fulfillment)
 *   • Compliance disclaimer BEFORE the chain
 *   • Every "Enter Research Select" → "View Products"
 *   • Per-card "View Products" CTA
 *   • Kickers ("END-TO-END TRACEABILITY", "THE PROCESS", "STEP 0X",
 *     "WHAT THAT MEANS FOR YOU", "RESEARCH 1617") are now dark ink for
 *     readability — gold reserved for large italic emphasis only.
 */

const CREAM = "#F1E5C7";
const INK = "#1B1614";
const BODY = "#4A4340";
const GOLD_ACCENT = "#7A5715";
const GOLD_FILL = "#DDA94E";
const HAIRLINE = "rgba(27,22,20,0.10)";
const CARD_BG = "#FBF3DD";

const CATALOG_HREF = "/shop?tier=select";

export default function SelectStory() {
  // Pulls live Select-tier products from the catalog so the section
  // reflects whatever admin has uploaded — no lock-in to hardcoded images.
  const [featuredProducts, setFeaturedProducts] = useState([]);
  // Admin-configurable marquee speed. Falls back to `slow` if the fetch fails
  // or the setting is not populated (matches the backend default).
  const [scrollSpeed, setScrollSpeed] = useState("slow");
  // iter148j — remember tier preference so shared pages (Contact, FAQ, policies)
  // can inherit the Select palette when the buyer's flow came from Select.
  useEffect(() => {
    try { localStorage.setItem("bbk_last_tier", "select"); } catch { /* no-op */ }
  }, []);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/products?tier=select");
        const list = Array.isArray(data) ? data : [];
        const inStock = list
          .filter((p) => p.active !== false && p.is_draft !== true)
          .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
        setFeaturedProducts(inStock);
      } catch (e) {
        setFeaturedProducts([]);
      }
    })();
    (async () => {
      try {
        const { data } = await api.get("/landing/settings");
        if (data?.select_scroll_speed) setScrollSpeed(data.select_scroll_speed);
      } catch (e) {
        /* keep default */
      }
    })();
  }, []);

  const marqueeClass =
    scrollSpeed === "glacial" ? "animate-bbk-marquee-glacial"
    : scrollSpeed === "fast" ? "animate-bbk-marquee-fast"
    : scrollSpeed === "normal" ? "animate-bbk-marquee"
    : "animate-bbk-marquee-slow";

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{ background: CREAM, color: INK }}
      data-testid="select-story-page"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
        aria-hidden
      />

      {/* HEADER — Select-tier logo, darkened for readability on cream */}
      <header className="relative" style={{ borderBottom: `1px solid ${HAIRLINE}` }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-28 flex items-center">
          <Link
            to="/shop?tier=select"
            className="inline-flex items-center group"
            data-testid="r1617-wordmark"
            aria-label="Research 1617"
          >
            <img
              src="/r1617-logo.png"
              alt="Research 1617"
              className="h-24 w-auto object-contain transition-opacity group-hover:opacity-90"
              style={{ filter: "brightness(0.45) saturate(1.6) contrast(1.2)" }}
              draggable={false}
            />
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="relative py-12 sm:py-16 lg:py-20">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12 text-center">
          <h1
            className="font-serif text-[28px] sm:text-[34px] lg:text-[40px] leading-[1.02] tracking-tight mb-5"
            data-testid="hero-headline"
          >
            We don&rsquo;t assume quality.{" "}
            <span className="italic" style={{ color: GOLD_ACCENT }}>
              We verify it.
            </span>
          </h1>
          <p
            className="text-[15px] sm:text-[16px] leading-relaxed max-w-xl mx-auto mb-8"
            style={{ color: BODY }}
            data-testid="hero-subhead"
          >
            Greater traceability from raw material to finished vial.
          </p>
          <PrimaryCta to={CATALOG_HREF} testid="story-hero-cta">
            View Products
          </PrimaryCta>
        </div>
      </section>

      {/* COMPLIANCE DISCLAIMER — new, before the chain */}
      <section className="relative pb-10 sm:pb-14">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12">
          <div
            className="relative p-5 sm:p-6 flex gap-4"
            style={{
              background: CARD_BG,
              border: `1px solid ${HAIRLINE}`,
              boxShadow: "0 12px 32px -24px rgba(27,22,20,0.2)",
            }}
            data-testid="compliance-disclaimer"
          >
            <span
              className="absolute inset-x-0 top-0 h-[2px]"
              style={{ background: GOLD_FILL }}
              aria-hidden
            />
            <span className="shrink-0 mt-1">
              <ShieldAlert size={18} strokeWidth={1.8} style={{ color: INK }} />
            </span>
            <div>
              <p
                className="text-[11px] uppercase tracking-[0.32em] font-mono mb-2"
                style={{ color: INK }}
              >
                Research Use Only
              </p>
              <p className="text-[13px] leading-relaxed" style={{ color: BODY }}>
                All products are intended solely for laboratory research and
                are not for human or animal consumption. By purchasing, the
                buyer agrees to use these products in compliance with all
                applicable laws. All products currently listed on this site
                are for research purposes ONLY.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* THREE STAGES — moved up so buyers see the how-it-works before the shelf */}
      <section className="relative pb-12 sm:pb-16" data-testid="chain-section">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-8">
            <p
              className="text-[12px] uppercase tracking-[0.32em] font-mono mb-3"
              style={{ color: INK }}
            >
              End-to-End Traceability
            </p>
            <h2
              className="font-serif text-[24px] sm:text-[28px] lg:text-[32px] tracking-tight leading-[1.08]"
              style={{ color: INK }}
            >
              Three stages.{" "}
              <span className="italic" style={{ color: GOLD_ACCENT }}>
                Two verifications.
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <ChainCard
              step="01"
              title="Raw API Testing"
              body="Sourced from vetted manufacturers and tested before it enters production."
            />
            <ChainCard
              step="02"
              title="US-Based Lyophilization"
              body="Qualified material is compounded and lyophilized in a controlled U.S. facility."
            />
            <ChainCard
              step="03"
              title="Third-Party COA"
              body="Independent analytical testing of the finished product. Lot-specific certificate on file."
            />
          </div>
        </div>
      </section>

      {/* EXPLORE OUR PRODUCTS — pulls live from Select catalog */}
      {featuredProducts.length > 0 && (
        <section className="relative pb-12 sm:pb-14" data-testid="explore-products-section">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
            <p
              className="text-[12px] uppercase tracking-[0.32em] font-mono mb-6 text-center"
              style={{ color: INK }}
            >
              Explore our curated Research products
            </p>
            {featuredProducts.length >= 4 ? (
              /* Auto-scrolling marquee — reuses the `bbk-marquee` keyframes
                 (0% → -50% translateX) so 2× repetition loops seamlessly.
                 Speed presets: glacial / slow / normal / fast. Pauses on hover
                 so buyers can linger on any card they're interested in. */
              <div
                className="relative overflow-hidden"
                data-testid="product-scroll-marquee"
              >
                <div
                  className={`bbk-marquee flex gap-5 hover:[animation-play-state:paused] ${marqueeClass}`}
                  style={{ width: "max-content" }}
                >
                  {[...featuredProducts, ...featuredProducts].map((p, i) => (
                    <ProductCard
                      key={`${p.id || p.slug}-${i}`}
                      product={p}
                    />
                  ))}
                </div>
              </div>
            ) : (
              /* Not enough products to loop cleanly — render a static row that
                 buyers can drag/swipe manually. */
              <div
                className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-6 px-6 lg:mx-0 lg:px-0"
                style={{ scrollbarWidth: "thin" }}
                data-testid="product-scroll-row"
              >
                {featuredProducts.map((p) => (
                  <ProductCard key={p.id || p.slug} product={p} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* CLOSING */}
      <section className="relative py-14 sm:py-16">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-12 text-center">
          <p
            className="text-[12px] uppercase tracking-[0.32em] font-mono mb-5"
            style={{ color: INK }}
          >
            What that means for you
          </p>
          <p
            className="font-serif text-[17px] sm:text-[20px] lg:text-[22px] leading-[1.25] tracking-tight"
            style={{ color: INK }}
          >
            Two stages of testing. A controlled domestic finishing process.{" "}
            <span className="italic" style={{ color: GOLD_ACCENT }}>
              Greater traceability
            </span>{" "}
            from raw material to finished vial.
          </p>
        </div>
      </section>

      {/* WHAT/WHY explanatory cards — Lyo, API, and the "why testing matters" essay */}
      <section className="relative pb-12 sm:pb-16" data-testid="glossary-section">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <GlossaryCard
              headline="What is Lyophilization?"
              testid="glossary-card-lyo"
              body={
                <>
                  <p>
                    Lyophilization, commonly called freeze-drying, removes
                    moisture from a product under carefully controlled
                    conditions.
                  </p>
                  <p>
                    For peptides, this turns the prepared solution into the
                    stable powder or &ldquo;puck&rdquo; you see inside the
                    vial. Proper lyophilization helps protect the product
                    during storage and provides consistency from vial to vial.
                  </p>
                  <p>
                    We compound and lyophilize our finished products in a
                    controlled U.S.-based facility.
                  </p>
                </>
              }
            />
            <GlossaryCard
              headline="What is API?"
              testid="glossary-card-api"
              body={
                <>
                  <p>
                    API stands for Active Pharmaceutical Ingredient. Simply
                    put, it&rsquo;s the raw active ingredient used to make the
                    finished product.
                  </p>
                  <p>Think of it as the starting material.</p>
                  <p>
                    Before we compound and lyophilize our raw peptide
                    material, we test it to verify what we&rsquo;re starting
                    with.
                  </p>
                  <p>
                    We test the raw material before it becomes the finished
                    product.
                  </p>
                </>
              }
            />
          </div>
          <GlossaryCard
            headline="Why does this matter?"
            testid="glossary-card-why"
            body={
              <>
                <p>
                  Most pharmaceutical ingredients aren&rsquo;t made in
                  America&mdash;and that alone isn&rsquo;t a quality problem.
                  According to the FDA, approximately 78% of API manufacturers
                  are located outside the United States. China and India are
                  major parts of the global pharmaceutical supply chain.
                </p>
                <p>
                  The issue isn&rsquo;t where an ingredient comes from. The
                  issue is whether you know what you&rsquo;re getting.
                  That&rsquo;s why our process adds verification at both ends:
                </p>
                <div
                  className="my-6 py-4 px-6 rounded-lg"
                  style={{
                    background: "rgba(221,169,78,0.12)",
                    border: `1px solid ${HAIRLINE}`,
                  }}
                >
                  <p
                    className="text-[12px] sm:text-[13px] uppercase tracking-[0.22em] font-mono text-center leading-relaxed"
                    style={{ color: GOLD_ACCENT }}
                  >
                    Raw Material Tested &rarr; US-Based Lyophilization &rarr;
                    Finished Product Independently Tested &rarr; Verifiable
                    3rd Party COA
                  </p>
                </div>
                <p>
                  We don&rsquo;t simply accept a supplier&rsquo;s claims or
                  paperwork. We test the raw material before production, and
                  an independent third-party laboratory tests the finished
                  product again.
                </p>
                <p className="italic" style={{ color: INK }}>
                  Two stages of testing. One verified finished product.
                </p>
              </>
            }
          />
        </div>
      </section>

      {/* COMPLIANCE FOOTER — brand tagline + contact + policy links + FDA disclaimer */}
      <footer className="relative pt-4 pb-12" data-testid="compliance-footer">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="relative overflow-hidden rounded-3xl px-6 sm:px-10 lg:px-14 py-8 sm:py-10"
            style={{
              background: "#FDF7E5",
              border: `1px solid ${HAIRLINE}`,
              boxShadow: "0 20px 60px -30px rgba(27,22,20,0.28)",
            }}
          >
            <span
              className="absolute inset-x-0 top-0 h-[2px]"
              style={{ background: GOLD_FILL }}
              aria-hidden
            />
            {/* Row 1 — tagline + contact */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 pb-8">
              <div>
                <p
                  className="text-[12px] uppercase tracking-[0.32em] font-mono mb-2"
                  style={{ color: INK }}
                >
                  Research 1617
                </p>
                <p
                  className="font-serif text-xl sm:text-2xl tracking-tight"
                  style={{ color: INK }}
                >
                  Ready when your work is.
                </p>
              </div>
              <div className="lg:text-right">
                <p className="text-[13px] leading-relaxed mb-4" style={{ color: BODY }}>
                  Need help? Text us or email — we reply within one business day.
                </p>
                <p className="text-[16px] leading-relaxed mb-1">
                  <a
                    href="sms:+19313270458"
                    className="hover:underline"
                    style={{ color: GOLD_ACCENT }}
                    data-testid="footer-phone"
                  >
                    (931) 327-0458
                  </a>
                  <span
                    className="text-[11px] uppercase tracking-[0.28em] font-mono ml-3"
                    style={{ color: BODY }}
                  >
                    Text only
                  </span>
                </p>
                <p className="text-[16px] leading-relaxed">
                  <a
                    href="mailto:support@bulkbuykits.com"
                    className="hover:underline"
                    style={{ color: GOLD_ACCENT }}
                    data-testid="footer-email"
                  >
                    support@bulkbuykits.com
                  </a>
                </p>
              </div>
            </div>

            {/* Row 2 — copyright + policy links */}
            <div
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 py-6"
              style={{ borderTop: `1px solid ${HAIRLINE}` }}
            >
              <p className="text-[13px]" style={{ color: BODY }}>
                © Research 1617 2026. All rights reserved.
              </p>
              <nav className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px]">
                <Link to="/contact" style={{ color: INK }} className="hover:opacity-70" data-testid="footer-link-contact">Contact</Link>
                <Link to="/policies/refunds" style={{ color: INK }} className="hover:opacity-70" data-testid="footer-link-refunds">Refunds</Link>
                <Link to="/policies/shipping" style={{ color: INK }} className="hover:opacity-70" data-testid="footer-link-shipping">Shipping</Link>
                <Link to="/policies/privacy" style={{ color: INK }} className="hover:opacity-70" data-testid="footer-link-privacy">Privacy</Link>
                <Link to="/policies/terms" style={{ color: INK }} className="hover:opacity-70" data-testid="footer-link-terms">Terms</Link>
              </nav>
            </div>

            {/* Row 3 — FDA / 503A / 503B disclaimer */}
            <div className="pt-6 space-y-4 text-[12px] leading-relaxed" style={{ color: BODY }}>
              <p>
                All products on this site are for research and development use only. Products are not for human consumption of any kind. The statements made on this website have not been evaluated by the US Food and Drug Administration. The statements and the products of this company are not intended to diagnose, treat, cure, or prevent any disease.
              </p>
              <p>
                Research 1617 is a chemical supplier. Research 1617 is not a compounding pharmacy or chemical compounding facility as defined under Section 503A of the Federal Food, Drug, and Cosmetic Act. Research 1617 is not an outsourcing facility as defined under Section 503B of the Federal Food, Drug, and Cosmetic Act.
              </p>
              <p>1617 17th Avenue South, Nashville, TN 37212</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ------------------------------- Parts ------------------------------- */

function ProductCard({ product }) {
  const href = `/shop/${product.slug}?tier=select`;
  // CAS Number lives inside product.spec as a {key,value} row (matches the
  // Admin → Edit Product spec editor). Look it up case-insensitively so both
  // "CAS Number" and "CAS number" (the two casings used across the codebase)
  // resolve correctly.
  const cas = (Array.isArray(product.spec) ? product.spec : []).find(
    (row) => typeof row?.key === "string" && row.key.toLowerCase().includes("cas")
  )?.value;
  return (
    <Link
      to={href}
      className="group relative flex flex-col flex-none w-[240px] sm:w-[260px] text-center transition-all hover:-translate-y-[2px] snap-start rounded-2xl overflow-hidden"
      style={{
        background: CARD_BG,
        border: `1px solid ${HAIRLINE}`,
        boxShadow: "0 12px 32px -24px rgba(27,22,20,0.25)",
      }}
      data-testid={`product-card-${product.slug}`}
    >
      <span
        className="absolute inset-x-0 top-0 h-[2px]"
        style={{ background: GOLD_FILL }}
        aria-hidden
      />
      <div className="pt-5 px-4">
        {cas ? (
          <p
            className="text-[10px] uppercase tracking-[0.28em] font-mono mb-2"
            style={{ color: INK, opacity: 0.7 }}
          >
            CAS #: {cas}
          </p>
        ) : (
          <div className="h-[16px] mb-2" aria-hidden />
        )}
        <h3
          className="font-serif text-[18px] leading-tight tracking-tight mb-4"
          style={{ color: INK }}
        >
          {product.name}
        </h3>
      </div>
      {/* Dark spotlight well — turns the black-on-black product photography
          into a jewelry-case display gesture. When admin re-shoots on cream,
          swap the `background` to CARD_BG and the inset shadow disappears. */}
      <div
        className="mx-4 mb-3 rounded-xl overflow-hidden"
        style={{
          background: INK,
          boxShadow: "inset 0 2px 12px rgba(0,0,0,0.35)",
          border: "1px solid rgba(221,169,78,0.15)",
        }}
      >
        <div className="flex items-center justify-center min-h-[170px] p-3">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.name}
              className="max-h-[160px] w-auto object-contain"
              draggable={false}
            />
          ) : (
            <span
              className="text-[11px] uppercase tracking-[0.28em] font-mono"
              style={{ color: GOLD_FILL, opacity: 0.55 }}
            >
              R1617
            </span>
          )}
        </div>
      </div>
      <div className="pb-5 flex justify-center">
        <span
          className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-[11px] uppercase tracking-[0.24em] font-mono transition-all group-hover:brightness-[1.08]"
          style={{
            background: GOLD_FILL,
            color: INK,
            border: `1px solid ${GOLD_FILL}`,
          }}
        >
          View Product
          <ArrowRight size={12} strokeWidth={2.4} />
        </span>
      </div>
    </Link>
  );
}

function GlossaryCard({ headline, body, testid }) {
  return (
    <div
      className="relative rounded-2xl p-8 sm:p-10"
      style={{
        background: "#FDF7E5",
        border: `1px solid ${HAIRLINE}`,
        boxShadow: "0 16px 40px -28px rgba(27,22,20,0.25)",
      }}
      data-testid={testid || "glossary-card"}
    >
      <span
        className="absolute inset-x-0 top-0 h-[2px] rounded-t-2xl"
        style={{ background: GOLD_FILL }}
        aria-hidden
      />
      <h3
        className="font-serif capitalize text-[22px] sm:text-[26px] tracking-tight leading-tight mb-5"
        style={{ color: INK }}
      >
        {headline}
      </h3>
      <div
        className="text-[15px] leading-relaxed space-y-4"
        style={{ color: BODY }}
      >
        {body}
      </div>
    </div>
  );
}

function ChainCard({ step, title, body }) {
  return (
    <div
      className="relative p-6 sm:p-7 flex flex-col text-center transition-all hover:-translate-y-[2px]"
      style={{
        background: CARD_BG,
        border: `1px solid ${HAIRLINE}`,
        boxShadow: "0 12px 32px -24px rgba(27,22,20,0.25)",
      }}
      data-testid={`chain-card-${step}`}
    >
      <span
        className="absolute inset-x-0 top-0 h-[2px]"
        style={{ background: GOLD_FILL }}
        aria-hidden
      />
      <p
        className="text-[11px] uppercase tracking-[0.32em] font-mono mb-4"
        style={{ color: INK }}
      >
        Step {step}
      </p>
      <h3
        className="font-serif text-[19px] sm:text-[21px] leading-tight tracking-tight mb-3"
        style={{ color: INK }}
      >
        {title}
      </h3>
      <p
        className="text-[14px] leading-relaxed mb-6 flex-1"
        style={{ color: BODY }}
      >
        {body}
      </p>
      <Link
        to={CATALOG_HREF}
        className="inline-flex items-center gap-2 self-center rounded-full px-6 py-2.5 text-[11px] uppercase tracking-[0.24em] font-mono transition-all hover:brightness-[1.08]"
        style={{
          background: GOLD_FILL,
          color: INK,
          border: `1px solid ${GOLD_FILL}`,
        }}
        data-testid={`chain-card-${step}-cta`}
      >
        View Products
        <ArrowRight size={13} strokeWidth={2.4} />
      </Link>
    </div>
  );
}

function PrimaryCta({ to, testid, large = false, children }) {
  const pad = large ? "px-9 py-4" : "px-8 py-3";
  const iconSize = large ? 16 : 14;
  return (
    <Link
      to={to}
      className={`inline-flex items-center gap-3 rounded-full ${pad} text-[14px] uppercase tracking-[0.24em] font-mono transition-all hover:brightness-[1.08]`}
      style={{
        background: GOLD_FILL,
        color: INK,
        border: `1px solid ${GOLD_FILL}`,
      }}
      data-testid={testid}
    >
      {children}
      <ArrowRight size={iconSize} strokeWidth={2.4} />
    </Link>
  );
}

function ResearchWordmark() {
  return null;
}
